import { FormEvent, useState } from 'react';
import { allSitters } from '../../../mocks/sitters';
import { BookingFormData } from './ServiceDateStep';
import { Link } from 'react-router-dom';

const serviceLabels: Record<string, { label: string; unit: string; priceAdd: number }> = {
  walk: { label: '犬の散歩', unit: '回', priceAdd: 0 },
  visit: { label: '訪問ケア', unit: '回', priceAdd: 5 },
  overnight: { label: 'お泊まり', unit: '泊', priceAdd: 25 },
  taxi: { label: 'タクシー送迎', unit: '回', priceAdd: 8 },
};

const GUARANTEE_FEE_PER_DAY = 2;

interface Props {
  selectedSitterId: number | null;
  formData: BookingFormData;
  onBack: () => void;
  onSuccess: () => void;
  formAction: string;
}

export default function ConfirmPayStep({ selectedSitterId, formData, onBack, onSuccess, formAction }: Props) {
  const sitter = allSitters.find(s => s.id === selectedSitterId);
  const svc = serviceLabels[formData.service];
  const basePrice = sitter?.pricePerVisit ?? 0;
  const servicePrice = svc ? basePrice + svc.priceAdd : 0;

  // 安心保証費用の日数計算
  const guaranteeDays = (() => {
    if (formData.service === 'overnight' && formData.startDate && formData.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      const diffMs = end.getTime() - start.getTime();
      const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      return Math.max(1, days);
    }
    return 1;
  })();

  const guaranteeFee = guaranteeDays * GUARANTEE_FEE_PER_DAY;

  // VAT登録ありのシッター：合計にVAT表示（NL 21% / DE 19%）
  // VAT登録なしのシッター：保証費用にのみ21%を内税で乗せる（お客さんには見えない）
  const isVatRegistered = sitter?.vatRegistered ?? false;

  const vatRate = isVatRegistered && sitter?.country === 'オランダ' ? 0.21
    : isVatRegistered && sitter?.country === 'ドイツ' ? 0.19
    : 0;

  // VAT登録なしの場合、保証費用に21%を内包して表示
  const displayedGuaranteeFee = isVatRegistered
    ? guaranteeFee
    : Math.round(guaranteeFee * 1.21 * 100) / 100;

  const subtotal = servicePrice + displayedGuaranteeFee;
  const vatAmount = isVatRegistered ? Math.round(subtotal * vatRate * 100) / 100 : 0;
  const vatLabel = isVatRegistered && sitter?.country === 'オランダ' ? 'BTW（VAT 21%）'
    : isVatRegistered && sitter?.country === 'ドイツ' ? 'MwSt（VAT 19%）'
    : null;
  const totalPrice = subtotal + vatAmount;

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [agree, setAgree] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const inputClass = 'w-full px-4 py-3 border border-[#D4E8EC] rounded-xl text-sm outline-none focus:border-[#81D8D0] transition-colors';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!agree) return;
    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      body.set('name', name);
      body.set('email', email);
      body.set('sitter', sitter?.name ?? '');
      body.set('service', svc?.label ?? formData.service);
      body.set('startDate', formData.startDate);
      body.set('endDate', formData.endDate);
      body.set('petType', formData.petType);
      body.set('petDetails', formData.petDetails);
      body.set('notes', formData.notes);
      body.set('guaranteeFee', isVatRegistered
        ? `€${guaranteeFee}（€2/日 × ${guaranteeDays}日）`
        : `€${displayedGuaranteeFee.toFixed(2)}`);
      if (vatLabel && vatAmount > 0) body.set('vat', `€${vatAmount.toFixed(2)}（${vatLabel}）`);
      body.set('totalPrice', `€${totalPrice.toFixed(2)}`);
      await fetch(formAction, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch {
      // proceed anyway
    } finally {
      setIsSubmitting(false);
      onSuccess();
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-[#1B2A4A] mb-2">内容確認・送信</h2>
      <p className="text-sm text-[#1B2A4A]/55 mb-6">内容をご確認の上、シッターにリクエストを送ってください</p>

      {/* Summary */}
      <div className="bg-[#EEF4F7] rounded-2xl p-4 sm:p-5 mb-6 space-y-3">
        <p className="text-xs font-semibold text-[#1B2A4A]/50 uppercase tracking-wider">予約内容</p>
        {[
          { label: 'シッター', value: sitter?.name ?? '—' },
          { label: 'サービス', value: svc ? svc.label : '—' },
          { label: '日程', value: formData.startDate ? new Date(formData.startDate).toLocaleString('ja-JP', { month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—' },
          { label: 'ペット', value: formData.petType || '—' },
        ].map(item => (
          <div key={item.label} className="flex items-center justify-between text-sm">
            <span className="text-[#1B2A4A]/55">{item.label}</span>
            <span className="font-semibold text-[#1B2A4A]">{item.value}</span>
          </div>
        ))}

        {/* 料金内訳 */}
        <div className="border-t border-[#D4E8EC] pt-3 space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-[#1B2A4A]/60">サービス料金</span>
            <span className="font-semibold text-[#1B2A4A]">€{servicePrice}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-shield-check-fill text-sm text-[#81D8D0]"></i>
              </div>
              <span className="text-[#1B2A4A]/60">
                安心保証費用
                {isVatRegistered && (
                  <span className="text-xs text-[#1B2A4A]/40 ml-1">
                    （€2/日 × {guaranteeDays}日）
                  </span>
                )}
              </span>
            </div>
            <span className="font-semibold text-[#81D8D0]">€{displayedGuaranteeFee.toFixed(2)}</span>
          </div>
          {vatLabel && (
            <div className="flex items-center justify-between text-sm">
              <div className="flex items-center gap-1.5">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-government-line text-sm text-[#1B2A4A]/40"></i>
                </div>
                <span className="text-[#1B2A4A]/60">{vatLabel}</span>
              </div>
              <span className="font-semibold text-[#1B2A4A]">€{vatAmount.toFixed(2)}</span>
            </div>
          )}
        </div>

        <div className="border-t border-[#D4E8EC] pt-3 flex items-center justify-between">
          <span className="text-sm font-bold text-[#1B2A4A]">合計（目安）</span>
          <span className="text-xl font-black text-[#81D8D0]">€{totalPrice.toFixed(2)}</span>
        </div>

        <Link
          to="/guarantee"
          className="flex items-center gap-1.5 pt-1"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-arrow-right-s-line text-sm text-[#81D8D0]"></i>
          </div>
          <span className="text-xs text-[#81D8D0] font-medium hover:underline">安心保証規約を確認する</span>
        </Link>
      </div>

      {/* Payment Flow Notice */}
      <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
        <i className="ri-information-line text-amber-500 text-lg mt-0.5 flex-shrink-0"></i>
        <div>
          <p className="text-sm font-semibold text-amber-800 mb-1">お支払いについて</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            リクエスト送信後、シッターが承認した場合のみお支払いが発生します。<br />
            承認のご連絡から<strong>48時間以内</strong>にお支払いをお願いします。
          </p>
        </div>
      </div>

      {/* Cancel Policy - prominent */}
      <div className="rounded-xl border-2 border-[#D4E8EC] overflow-hidden mb-6">
        <div className="flex items-center gap-2 bg-[#EEF4F7] px-4 py-3 border-b border-[#D4E8EC]">
          <div className="w-6 h-6 flex items-center justify-center">
            <i className="ri-shield-check-line text-[#81D8D0] text-base"></i>
          </div>
          <p className="text-sm font-bold text-[#1B2A4A]">キャンセルポリシー</p>
        </div>
        <div className="px-4 py-4 space-y-2.5">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-5 h-5 bg-emerald-100 rounded-full flex items-center justify-center mt-0.5">
              <i className="ri-check-line text-emerald-600 text-xs"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-[#1B2A4A]">予約日の48時間前まで</p>
              <p className="text-xs text-[#1B2A4A]/60 mt-0.5">無料でキャンセル可能</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center mt-0.5">
              <i className="ri-error-warning-line text-amber-500 text-xs"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-[#1B2A4A]">予約日の24時間以内</p>
              <p className="text-xs text-[#1B2A4A]/60 mt-0.5">お支払い額の<strong className="text-[#1B2A4A]">50%</strong>を請求</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mt-0.5">
              <i className="ri-close-line text-red-500 text-xs"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-[#1B2A4A]">無断キャンセル（ノーショー）</p>
              <p className="text-xs text-[#1B2A4A]/60 mt-0.5">お支払い額の<strong className="text-[#1B2A4A]">100%</strong>を請求</p>
            </div>
          </div>
          <div className="flex items-start gap-3 pt-1 border-t border-[#EEF4F7]">
            <span className="flex-shrink-0 w-5 h-5 bg-[#81D8D0]/20 rounded-full flex items-center justify-center mt-0.5">
              <i className="ri-user-line text-[#81D8D0] text-xs"></i>
            </span>
            <div>
              <p className="text-sm font-semibold text-[#1B2A4A]">シッター都合のキャンセル</p>
              <p className="text-xs text-[#1B2A4A]/60 mt-0.5">お支払い済みの場合は<strong className="text-[#1B2A4A]">全額返金</strong></p>
            </div>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} data-readdy-form id="booking-confirm-form" className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-[#1B2A4A] mb-2">お名前 <span className="text-red-400">*</span></label>
          <input
            type="text"
            name="name"
            value={name}
            onChange={e => setName(e.target.value)}
            required
            placeholder="山田 太郎"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#1B2A4A] mb-2">メールアドレス <span className="text-red-400">*</span></label>
          <input
            type="email"
            name="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            placeholder="example@email.com"
            className={inputClass}
          />
        </div>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={agree}
            onChange={e => setAgree(e.target.checked)}
            required
            className="mt-1 w-5 h-5 accent-[#81D8D0] cursor-pointer"
          />
          <span className="text-xs text-[#1B2A4A]/70 leading-relaxed">
            <Link to="/terms" className="text-[#81D8D0] hover:underline">利用規約</Link>・
            <Link to="/guarantee" className="text-[#81D8D0] hover:underline">安心保証規約</Link>・
            <Link to="/privacy" className="text-[#81D8D0] hover:underline">プライバシーポリシー</Link>に同意します
          </span>
        </label>

        <div className="flex gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="flex-1 py-4 bg-[#EEF4F7] text-[#1B2A4A] font-semibold rounded-xl hover:bg-[#D4E8EC] transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-arrow-left-line mr-2"></i>戻る
          </button>
          <button
            type="submit"
            disabled={isSubmitting || !agree || !name || !email}
            className="flex-[2] py-4 bg-[#81D8D0] text-white font-bold rounded-xl hover:bg-[#1B2A4A] transition-all disabled:bg-[#D4E8EC] disabled:cursor-not-allowed whitespace-nowrap"
          >
            {isSubmitting
              ? <span className="flex items-center justify-center gap-2"><i className="ri-loader-4-line animate-spin"></i>送信中...</span>
              : <span><i className="ri-send-plane-line mr-2"></i>リクエストを送る</span>}
          </button>
        </div>
      </form>
    </div>
  );
}
